// The chat's markdown (escapeHtml, stripSystemNotes, renderMarkdown, renderInline), shared by the chat's React
// views (through @cortex/sdk-chat-client) and an app's own chat UI (through @cortex/app-sdk/chat). Its XSS rule stands: every text
// fragment passes through escapeHtml BEFORE any markdown -> HTML transform, so raw input HTML never
// reaches the DOM. An in-app link (/p/...) becomes a.in-app-link that routes the host SPA.
/* eslint-disable */

export function escapeHtml(s: unknown): string {
  return String(s).replace(
    /[&<>"']/g,
    (c) =>
      (
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }) as Record<string, string>
      )[c]!,
  );
}
// Server-injected context riding inside a user message (ws-handler appends
// the current background-investigations status so the model never answers
// from stale memory). It is model plumbing, not the user's words — hide it
// from the rendered bubble.
export function stripSystemNotes(text: unknown): string {
  return String(text).replace(
    /\n*\[(?:system note[^\]]*investigations|investigations status)[^\]]*\]\s*$/i,
    '',
  );
}

export function renderMarkdown(src: string): string {
  if (!src) return '';
  const lines = src.split('\n');
  let out = '';
  let inFence = false;
  let fenceBuf: string[] = [];
  let fenceLang = '';
  let para: string[] = [];
  let list: 'ul' | 'ol' | null = null;
  let tableBuf: string[] | null = null; // buffered consecutive |...| lines (GH table candidate)
  let quote: string[] | null = null; // buffered > blockquote lines
  const flushPara = () => {
    if (para.length === 0) return;
    out += '<p>' + renderInline(para.join(' ')) + '</p>';
    para = [];
  };
  const flushList = () => {
    if (list) {
      out += `</${list}>`;
      list = null;
    }
  };
  const flushQuote = () => {
    if (!quote) return;
    out += '<blockquote>' + renderInline(quote.join(' ')) + '</blockquote>';
    quote = null;
  };
  // Github-style table: header row, |---| separator row, then data rows.
  // Cells keep inline md (renderInline escapes first — same XSS discipline
  // as prose). A pipe-run WITHOUT a separator row is not a table → falls
  // back to paragraph text.
  const isTableSep = (ln: string) => /^\s*\|?[\s:|-]+\|?\s*$/.test(ln) && ln.includes('-');
  const parseTableRow = (ln: string) => {
    let s = ln.trim();
    if (s.startsWith('|')) s = s.slice(1);
    if (s.endsWith('|')) s = s.slice(0, -1);
    return s.split('|').map((c) => c.trim());
  };
  const flushTable = () => {
    if (!tableBuf) return;
    const rows = tableBuf;
    tableBuf = null;
    if (rows.length >= 2 && isTableSep(rows[1]!)) {
      const head = parseTableRow(rows[0]!);
      out +=
        '<table class="md-table"><thead><tr>' +
        head.map((c: string) => '<th>' + renderInline(c) + '</th>').join('') +
        '</tr></thead><tbody>';
      for (const r of rows.slice(2)) {
        if (isTableSep(r)) continue;
        const cells = parseTableRow(r);
        out +=
          '<tr>' +
          head
            .map((_: string, i: number) => '<td>' + renderInline(cells[i] != null ? cells[i] : '') + '</td>')
            .join('') +
          '</tr>';
      }
      out += '</tbody></table>';
    } else {
      for (const r of rows) para.push(r);
      flushPara();
    }
  };
  for (const raw of lines) {
    const row = raw.replace(/\s+$/, '');
    if (row.startsWith('```')) {
      if (inFence) {
        out += `<pre><code>${escapeHtml(fenceBuf.join('\n'))}</code></pre>`;
        fenceBuf = [];
        inFence = false;
        fenceLang = '';
      } else {
        flushPara();
        flushList();
        flushQuote();
        flushTable();
        inFence = true;
        fenceLang = row.slice(3).trim();
      }
      continue;
    }
    if (inFence) {
      fenceBuf.push(raw);
      continue;
    }
    if (!row.trim()) {
      flushPara();
      flushList();
      flushQuote();
      flushTable();
      continue;
    }
    // Table rows buffer BEFORE the other block checks. This is exactly what
    // used to collapse tables: unhandled | rows fell through to the paragraph
    // accumulator, whose flush JOINS consecutive lines with a space — so every
    // row of a table (newlines intact off the wire) merged into one line.
    if (/^\s*\|.*\|\s*$/.test(row)) {
      flushPara();
      flushList();
      flushQuote();
      if (!tableBuf) tableBuf = [];
      tableBuf.push(row);
      continue;
    }
    flushTable();
    // ATX headings (# .. ######)
    const h = row.match(/^(#{1,6})\s+(.+)$/);
    if (h) {
      flushPara();
      flushList();
      flushQuote();
      const lvl = h[1]!.length;
      out += `<h${lvl}>` + renderInline(h[2]!) + `</h${lvl}>`;
      continue;
    }
    // Horizontal rule: ---, ***, ___ alone on a line
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(row)) {
      flushPara();
      flushList();
      flushQuote();
      out += '<hr>';
      continue;
    }
    // Blockquote (single level; consecutive > lines merge into one block)
    const q = row.match(/^>\s?(.*)$/);
    if (q) {
      flushPara();
      flushList();
      if (!quote) quote = [];
      quote.push(q[1]!);
      continue;
    }
    flushQuote();
    const ul = row.match(/^[-*]\s+(.+)$/);
    const ol = row.match(/^\d+\.\s+(.+)$/);
    if (ul) {
      flushPara();
      if (list !== 'ul') {
        flushList();
        out += '<ul>';
        list = 'ul';
      }
      out += '<li>' + renderInline(ul[1]!) + '</li>';
      continue;
    }
    if (ol) {
      flushPara();
      if (list !== 'ol') {
        flushList();
        out += '<ol>';
        list = 'ol';
      }
      out += '<li>' + renderInline(ol[1]!) + '</li>';
      continue;
    }
    flushList();
    para.push(row);
  }
  flushPara();
  flushList();
  flushQuote();
  flushTable();
  if (inFence && fenceBuf.length) {
    out += `<pre><code>${escapeHtml(fenceBuf.join('\n'))}</code></pre>`;
  }
  return out;
}
export function renderInline(s: string): string {
  let html = escapeHtml(s);
  // code spans first (escape inside)
  html = html.replace(/`([^`]+)`/g, (_: string, c: string) => `<code>${c}</code>`);
  // links [text](url). An IN-APP path (a root-relative one, /…) must route
  // the host SPA, not open a second tab: the chat answers questions about
  // documents by handing back a link, and a new tab would reload the whole
  // app and lose this conversation. Anything external keeps target=_blank.
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_: string, t: string, u: string) =>
    /^\/(?!\/)/.test(u)
      ? `<a href="${u}" class="in-app-link" data-path="${u}">${t}</a>`
      : `<a href="${u}" target="_blank" rel="noopener">${t}</a>`,
  );
  // bold
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // italic (lone asterisks)
  html = html.replace(/(^|\s)\*([^*\s][^*]*)\*(?=\s|$)/g, '$1<em>$2</em>');
  return html;
}
